import {
  DefaultError,
  InfiniteData,
  QueryFunction,
  QueryKey,
  QueryStatus,
  UndefinedInitialDataInfiniteOptions,
  useInfiniteQuery as useReactQueryInfinite,
  useMutation as useReactQueryMutation,
  UseMutationOptions,
  useQuery as useReactQuery,
  UseQueryOptions,
} from '@tanstack/react-query';
import { useMemo } from 'react';

export type { QueryStatus } from '@tanstack/react-query';
export {
  QueryClient,
  QueryClientProvider,
  useQueries,
} from '@tanstack/react-query';

import { queryClient } from '@/queryclient';
import { JobStatus } from '@/types';
import { createError } from '@/utils/error.util';
import { capitalize } from '@/utils/lodash.util';
import { appendPeriodIfMissing } from '@/utils/text.util';

/** Element-wise prefix match — the hand-rolled invalidation semantics. */
function isKeyPrefixMatch(candidateKey: QueryKey, prefix: QueryKey): boolean {
  return (
    candidateKey.length >= prefix.length &&
    prefix.every((segment, index) => candidateKey[index] === segment)
  );
}

/**
 * Invalidation-key matcher that understands the user-scope suffix
 * `useGetUserKey` appends as the LAST key segment (ENG-4400).
 *
 * Plain element-wise prefix matching silently misses when a narrow
 * invalidation key ends with the friend (`[MY, resource, friend]`) but the
 * cached key carries variable middle segments before the same friend
 * (`[MY, resource, '"query"', friend]`): index 2 compares friend against the
 * query string and never matches. So, in addition to the plain prefix match,
 * when BOTH keys end with the exact same non-empty `userKeySuffix` (the
 * CURRENT principal only — a key scoped to a different friend can never take
 * this branch, preserving tenant isolation), match the prefix with the
 * trailing suffix stripped from both sides.
 *
 * With no `userKeySuffix` this is exactly the legacy prefix predicate.
 */
export function matchesUserScopedKey(
  candidateKey: QueryKey,
  invalidationKey: QueryKey,
  userKeySuffix?: string
): boolean {
  if (isKeyPrefixMatch(candidateKey, invalidationKey)) {
    return true;
  }

  if (
    !userKeySuffix ||
    invalidationKey[invalidationKey.length - 1] !== userKeySuffix ||
    candidateKey[candidateKey.length - 1] !== userKeySuffix
  ) {
    return false;
  }

  return isKeyPrefixMatch(
    candidateKey.slice(0, -1),
    invalidationKey.slice(0, -1)
  );
}

/**
 * Write-scoping matcher for optimistic cache WRITES (updateAllSubQueries →
 * updateAllQueryCache). STRICTER than {@link matchesUserScopedKey}, which backs
 * the read-only `invalidate()`.
 *
 * `matchesUserScopedKey`'s plain-prefix branch is safe to *invalidate* through
 * but leaks on a *write* for the non-impersonating ("me") principal: "me"'s
 * conversation-list key is the UNSUFFIXED short key
 * `[MY, conversation, '"parent_id:self"']`, which is a strict element-wise
 * prefix of EVERY friend-suffixed sibling
 * `[MY, conversation, '"parent_id:self"', friend]`. So a "me" optimistic
 * prepend would prefix-match — and overwrite — a friend's cached list
 * (ENG-4400 defense-in-depth; production only avoids it today because
 * `queryClient.clear()` evicts friend queries on every principal switch —
 * closed here structurally rather than by clear-timing).
 *
 * Rule:
 *  - impersonating (non-empty `userKeySuffix`): defer to
 *    `matchesUserScopedKey`. Its suffix branch already rejects a DIFFERENT
 *    friend's key, and its plain-prefix branch cannot reach a foreign key
 *    because the target's own friend suffix sits mid-key and blocks the
 *    element-wise compare. Legitimate same-principal sub-queries (same trailing
 *    friend, extra middle segments) still match — the current-principal write
 *    is NOT weakened.
 *  - "me" (empty `userKeySuffix`): the principal's key carries NO trailing
 *    suffix, so its own query IS the key exactly. `useGetUserKey` only ever
 *    appends ONE trailing suffix segment, so any LONGER active candidate is a
 *    foreign-suffixed sibling — never a legitimate same-principal sub-query in
 *    the user-scoped flow. Require exact key length so a "me" write can never
 *    land in a friend-suffixed key.
 *
 * The sole caller (useChatWorker → useGetConversations → useMy) is user-scoped
 * and its "me" target is a COMPLETE key, so exact-match-on-empty-suffix loses
 * no legitimate write. A future non-user-scoped caller that legitimately needs
 * a broad prefix write would have to opt in explicitly rather than reopen this
 * hole.
 */
export function matchesOwnUserScopedKey(
  candidateKey: QueryKey,
  targetKey: QueryKey,
  userKeySuffix?: string
): boolean {
  if (!matchesUserScopedKey(candidateKey, targetKey, userKeySuffix)) {
    return false;
  }

  if (!userKeySuffix) {
    return candidateKey.length === targetKey.length;
  }

  return true;
}

interface UserScopedInvalidateOptions {
  /**
   * The user-scope segment (impersonated friend or explicit email) that
   * `useGetUserKey` appended as the last segment of `queryKey`, when any.
   * Makes `invalidate()` friend-aware via {@link matchesUserScopedKey}.
   * Omitted/empty = legacy element-wise prefix matching, unchanged.
   */
  userKeySuffix?: string;
}

export function useMutation<
  TData = unknown,
  TError = DefaultError,
  TVariables = void,
  TContext = unknown,
>(
  options: UseMutationOptions<TData, TError, TVariables, TContext> &
    CustomOptions<TVariables> &
    Required<
      Pick<
        UseMutationOptions<TData, TError, TVariables, TContext>,
        'mutationFn'
      >
    >
) {
  return useReactQueryMutation({
    ...options,
    mutationFn: async (...args) => {
      try {
        return await options.mutationFn(...args);
      } catch (error) {
        throw getQueryError(options, error, args?.[0]);
      }
    },
  });
}

export function useQuery<
  TQueryFnData = unknown,
  TError = DefaultError,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
>(
  options: UseQueryOptions<TQueryFnData, TError, TData, TQueryKey> &
    CustomOptions<void> &
    UserScopedInvalidateOptions & {
      queryFn: QueryFunction<TQueryFnData, TQueryKey, never>;
    }
) {
  const query = useReactQuery({
    ...options,
    queryFn: async (...args) => {
      try {
        return await options.queryFn(...args);
      } catch (error) {
        throw getQueryError(options, error, undefined);
      }
    },
  });

  return {
    ...query,
    updateAllSubQueries: (data: (prevData: TData) => TData) => {
      updateAllQueryCache<TData>(options.queryKey, data, options.userKeySuffix);
    },
    invalidate: () => {
      queryClient.invalidateQueries({
        predicate: query =>
          matchesUserScopedKey(
            query.queryKey,
            options.queryKey,
            options.userKeySuffix
          ),
      });
    },
  };
}

export function useInfiniteQuery<
  TQueryFnData,
  TError = DefaultError,
  TData = InfiniteData<InfinityData<TQueryFnData>>,
  TQueryKey extends QueryKey = QueryKey,
  TPageParam = unknown,
>(
  options: UndefinedInitialDataInfiniteOptions<
    InfinityData<TQueryFnData>,
    TError,
    TData,
    TQueryKey,
    TPageParam
  > &
    CustomOptions<void> &
    UserScopedInvalidateOptions & {
      queryFn: QueryFunction<InfinityData<TQueryFnData>, TQueryKey, TPageParam>;
    }
) {
  const query = useReactQueryInfinite({
    ...options,
    queryFn: async (...args) => {
      try {
        return await options.queryFn(...args);
      } catch (error) {
        throw getQueryError(options, error, undefined);
      }
    },
  });

  return {
    ...query,
    updateAllSubQueries: (
      data: (
        currentValue: InfiniteData<InfinityData<TQueryFnData>>
      ) => InfiniteData<InfinityData<TQueryFnData>>
    ) => {
      updateAllQueryCache(options.queryKey, data, options.userKeySuffix);
    },
    invalidate: () => {
      queryClient.invalidateQueries({
        predicate: query =>
          matchesUserScopedKey(
            query.queryKey,
            options.queryKey,
            options.userKeySuffix
          ),
      });
    },
  };
}

export interface InfinityData<TData> {
  count?: number;
  data: TData;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  offset: any;
  // Keyset (cursor) pagination next-page token. Present instead of `offset`
  // when the backend engaged cursor mode (query carried structured
  // OrderByFields). Absent when there are no more pages. Legacy offset paging
  // never sets this. See useMy / useGenericSearch `cursorPagination`.
  cursor?: string;
}

export type UseExtendInfiniteQueryOptions<TData> = Omit<
  UndefinedInitialDataInfiniteOptions<InfinityData<TData>, Error>,
  'queryKey' | 'getNextPageParam' | 'initialPageParam'
> & {
  email?: string;
  // Opt-in keyset (cursor) pagination. Default undefined/false = legacy offset
  // paging, byte-identical to before. When true, the hook pages by an opaque
  // cursor token instead of a numeric offset (falls back to offset paging when
  // the query's sort can't engage keyset on the backend). The flag takes effect
  // for both POST graph-query requests and string-key GET requests: page 1 sends
  // the empty-string cursor sentinel and resumes via the echoed token, falling
  // back transparently to offset paging when the backend emits no token.
  cursorPagination?: boolean;
  // Opt-in fetch cancellation. Default undefined/false = the queryFn never
  // touches TanStack's abort signal, so an in-flight request keeps running to
  // completion after the last observer unmounts and still warms the cache —
  // behavior ENG-4944's decoupled wait-and-reapply depends on. When true, the
  // queryFn consumes the per-fetch AbortSignal and forwards it to axios, so
  // last-observer unsubscribe (drawer close, key-remount) aborts the request.
  abortOnUnmount?: boolean;
};

export type UseExtendQueryOptions<TData> = Omit<
  UseQueryOptions<TData, Error>,
  'queryFn' | 'queryKey'
> & {
  doNotImpersonate?: boolean;
  email?: string;
};

function updateAllQueryCache<T>(
  key: QueryKey,
  data: (currentValue: T) => T,
  userKeySuffix?: string
) {
  const queryCache = queryClient.getQueryCache();

  // Route the write through the STRICT own-principal matcher instead of
  // TanStack's coarse element-wise prefix match (`{ queryKey: key }`). The
  // coarse match treats "me"'s unsuffixed key as a prefix of every
  // friend-suffixed sibling, so an optimistic "me" prepend would write into a
  // friend's active list. matchesOwnUserScopedKey closes that structurally
  // (ENG-4400 defense-in-depth) while still hitting the real current-principal
  // query, impersonating or not.
  queryCache
    .findAll({
      type: 'active',
      predicate: cache =>
        matchesOwnUserScopedKey(cache.queryKey, key, userKeySuffix),
    })
    .forEach(cache => {
      queryClient.setQueryData(cache.queryKey, data);
    });
}

function getQueryError<TVariable>(
  options: CustomOptions<TVariable>,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  error: any,
  variable: TVariable
) {
  const isLicenseError = error?.response?.status === 402;
  const errorFromResponse = options.getErrorFromResponse
    ? error.response?.data
    : '';
  const errorMessageByStatusCode =
    options.errorByStatusCode &&
    (typeof options.errorByStatusCode === 'function'
      ? options.errorByStatusCode(variable)[error?.response?.status]
      : options.errorByStatusCode[error?.response?.status]);

  const defaultError =
    typeof options.defaultErrorMessage === 'function'
      ? options.defaultErrorMessage(variable)
      : options.defaultErrorMessage;

  const queryErrorTitle =
    errorFromResponse || errorMessageByStatusCode || defaultError;
  let queryErrorMessage = '';

  if (isLicenseError && !errorMessageByStatusCode) {
    queryErrorMessage = appendContactSupport('License Required');
  } else {
    queryErrorMessage = appendContactSupport('');
  }

  return createError(
    queryErrorMessage,
    queryErrorTitle,
    error?.response?.status
  );
}

function appendContactSupport(error: string) {
  const support = 'Please contact support@praetorian.com.';

  return capitalize(
    error ? appendPeriodIfMissing(error) + ` ${support}` : support
  );
}

interface CustomOptions<TVariables> {
  defaultErrorMessage: string | ((variable: TVariables) => string);
  errorByStatusCode?:
    | Record<number, string>
    | ((variable: TVariables) => Record<number, string>);
  getErrorFromResponse?: boolean;
}

export function mergeJobStatus(
  jobStatus: (JobStatus | undefined)[]
): JobStatus | undefined {
  const defined = jobStatus.filter((s): s is JobStatus => s !== undefined);

  if (defined.length === 0) {
    return undefined;
  }

  // Active jobs take priority (unchanged)
  if (
    defined.includes(JobStatus.Running) ||
    defined.includes(JobStatus.AsyncRunning) ||
    defined.includes(JobStatus.AsyncReturning)
  ) {
    return JobStatus.Running;
  }
  if (defined.includes(JobStatus.Queued)) {
    return JobStatus.Queued;
  }

  // All remaining are terminal: Pass or Fail
  const hasFail = defined.includes(JobStatus.Fail);
  const hasPass = defined.includes(JobStatus.Pass);

  if (hasFail && hasPass) {
    return JobStatus.Partial; // THE BUG FIX
  }
  if (hasFail) {
    return JobStatus.Fail;
  }
  if (hasPass) {
    return JobStatus.Pass;
  }

  return undefined;
}

/**
 * This util combines status from multiple queries into one status
 * @param statusList List of QueryStatus
 * @returns
 */
export function mergeStatus(...statusList: QueryStatus[]): QueryStatus {
  let isError = false;
  let isLoading = false;

  statusList.forEach(s => {
    if (s === 'error') {
      isError = true;
    }
    if (s === 'pending') {
      isLoading = true;
    }
  });

  if (isError) {
    return 'error';
  } else if (isLoading) {
    return 'pending';
  }

  return 'success';
}

export function useMergeStatus(...statusList: QueryStatus[]): QueryStatus {
  // eslint-disable-next-line react-hooks/use-memo
  return useMemo(() => mergeStatus(...statusList), [statusList.join(',')]);
}
